/**
 * Co-Pilot Settings Tab Component
 * Manages LLM provider configuration and MCP server settings for Converse Co-Pilot v4.0
 */

import React, { useState, useEffect } from 'react';

import {
  Brain,
  Server,
  BookOpen,
  Code2,
  Save,
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { Skeleton } from '../ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { toast } from '../ui/use-toast';
import api from '../../services/api';
import { API_BASE_URL } from '../../config/api';

interface LLMProvider {
  id: string;
  name: string;
  models: {
    id: string;
    name: string;
    context_window: number;
    supports_tools: boolean;
  }[];
}

interface MCPServer {
  id: number;
  name: string;
  description?: string;
  config: any;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface CoPilotSettings {
  enabled: boolean;
  llm_provider: string;
  llm_model: string;
  api_key?: string;
  api_key_configured?: boolean;
  temperature: number;
  max_tokens: number;
  system_prompt?: string;
}

interface KnowledgeBaseSettings {
  embedding_provider: string;
  embedding_model: string;
  embedding_dimension: number;
  openai_model?: string;
  openai_api_key?: string;
  has_openai_key?: boolean;
  max_chunk_tokens: number;
  chunk_overlap_tokens: number;
  batch_size: number;
  is_configured: boolean;
  last_updated?: string;
}

const CoPilotSettingsTab: React.FC = () => {
  // LLM Settings State
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [settings, setSettings] = useState<CoPilotSettings>({
    enabled: true,
    llm_provider: 'anthropic',
    llm_model: 'claude-3-5-sonnet-20241022',
    temperature: 0.7,
    max_tokens: 4096,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // MCP Servers State
  const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
  const [serverToolCounts, setServerToolCounts] = useState<Record<number, number>>({});
  const [mcpLoading, setMcpLoading] = useState(false);
  const [showMcpSheet, setShowMcpSheet] = useState(false);
  const [editingMcp, setEditingMcp] = useState<MCPServer | null>(null);
  const [mcpForm, setMcpForm] = useState({
    name: '',
    description: '',
    config: '{\n  "command": "npx",\n  "args": ["-y", "@package/server"],\n  "env": {}\n}',
  });
  const [deleteMcpTarget, setDeleteMcpTarget] = useState<{ id: number; name: string } | null>(null);

  // Knowledge Base Settings State
  const [kbSettings, setKbSettings] = useState<KnowledgeBaseSettings>({
    embedding_provider: 'local',
    embedding_model: 'paraphrase-MiniLM-L3-v2',
    embedding_dimension: 384,
    max_chunk_tokens: 512,
    chunk_overlap_tokens: 50,
    batch_size: 16,
    is_configured: false,
  });
  const [kbLoading, setKbLoading] = useState(false);
  const [kbSaving, setKbSaving] = useState(false);

  // UI State
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const [testingServer, setTestingServer] = useState<number | null>(null);
  const [loadingModels, setLoadingModels] = useState(false);
  const [refreshingTools, setRefreshingTools] = useState(false);

  useEffect(() => {
    loadProviders();
    loadCoPilotSettings();
    loadMcpServers();
    loadKBSettings();
  }, []);

  useEffect(() => {
    if (settings.api_key && settings.api_key.trim() !== '') {
      fetchDynamicModels();
    }
  }, [settings.api_key, settings.llm_provider]);

  const loadProviders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/copilot-settings/providers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers);
      }
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  };

  const loadCoPilotSettings = async () => {
    setSettingsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/copilot-settings/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading Co-Pilot settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchDynamicModels = async () => {
    if (!settings.llm_provider || !settings.api_key || settings.api_key.trim() === '') {
      return;
    }
 
    setLoadingModels(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        api_key: settings.api_key
      });
 
      const response = await fetch(
        `${API_BASE_URL}/copilot-settings/models/${settings.llm_provider}?${params}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
 
      if (response.ok) {
        const data = await response.json();
        // Update the provider's models in the providers state
        setProviders(prevProviders =>
          prevProviders.map(provider =>
            provider.id === settings.llm_provider
              ? { ...provider, models: data.models }
              : provider
          )
        );
      }
    } catch (error) {
      console.error('Error fetching dynamic models:', error);
      // Silently fail and use static models
    } finally {
      setLoadingModels(false);
    }
  };

  const saveCoPilotSettings = async () => {
    setSettingsSaving(true);
    try {
      const token = localStorage.getItem('token');

      // Only send fields that backend accepts (exclude api_key_configured)
      const updatePayload = {
        enabled: settings.enabled,
        llm_provider: settings.llm_provider,
        llm_model: settings.llm_model,
        api_key: settings.api_key,
        temperature: settings.temperature,
        max_tokens: settings.max_tokens,
        system_prompt: settings.system_prompt,
      };

      const response = await fetch(`${API_BASE_URL}/copilot-settings/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Co-Pilot settings saved successfully!', severity: 'success' });
      } else {
        const errorData = await response.json();
        console.error('Error saving Co-Pilot settings:', errorData);
        throw new Error(errorData.detail || 'Failed to save settings');
      }
    } catch (error: any) {
      toast({
        title: error?.response?.data?.detail || 'Failed to save Co-Pilot settings',
        variant: 'destructive',
      });
    } finally {
      setSettingsSaving(false);
    }
  };

  const loadMcpServers = async () => {
    setMcpLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/mcp-servers/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMcpServers(data);
      }
    } catch (error) {
      console.error('Error loading MCP servers:', error);
    } finally {
      setMcpLoading(false);
    }
  };

  const saveMcpServer = async () => {
    try {
      const token = localStorage.getItem('token');
 
      // Parse and validate config JSON
      let configJson;
      try {
        configJson = JSON.parse(mcpForm.config);
      } catch {
        setSnackbar({ open: true, message: 'Invalid JSON configuration', severity: 'error' });
        return;
      }
 
      const endpoint = editingMcp
        ? `${API_BASE_URL}/mcp-servers/${editingMcp.id}`
        : `${API_BASE_URL}/mcp-servers/`;
 
      const method = editingMcp ? 'PUT' : 'POST';
 
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: mcpForm.name,
          description: mcpForm.description,
          config: configJson,
          enabled: true
        })
      });
 
      if (response.ok) {
        setSnackbar({
          open: true,
          message: `MCP server ${editingMcp ? 'updated' : 'created'} successfully!`,
          severity: 'success'
        });
        setShowMcpSheet(false);
        loadMcpServers();
        resetMcpForm();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save MCP server');
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to save MCP server', severity: 'error' });
    }
  }
  
 
  const handleDeleteMcpServer = async () => {
    if (!deleteMcpTarget) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/mcp-servers/${deleteMcpTarget.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'MCP server deleted successfully!', severity: 'success' });
        loadMcpServers();
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete MCP server', severity: 'error' });
    }
  };

  const testMcpServer = async (id: number) => {
    setTestingServer(id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/mcp-servers/${id}/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      const toolsCount = Number(result.tools_available ?? result.tools?.length ?? 0);

      if (response.ok && result.success) {
        setServerToolCounts((prev) => ({ ...prev, [id]: toolsCount }));
        const message =
          result.message ||
          `Successfully connected — ${toolsCount} MCP tools connected`;
        setSnackbar({
          open: true,
          message,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.detail || result.message || 'Connection test failed',
          severity: 'error'
        });
      }
    } catch {
      toast({ title: 'Failed to test MCP server connection', variant: 'destructive' });
    } finally {
      setTestingServer(null);
    }
  };

  const toggleMcpServerEnabled = async (server: MCPServer) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/mcp-servers/${server.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: !server.enabled
        })
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: `MCP server ${!server.enabled ? 'enabled' : 'disabled'} successfully!`,
          severity: 'success'
        });
        loadMcpServers();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update MCP server');
      }
    } catch (error: any) {
      toast({
        title: error?.response?.data?.detail || 'Failed to update MCP server status',
        variant: 'destructive',
      });
    }
  };

  const refreshMcpTools = async () => {
    setRefreshingTools(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/mcp-servers/refresh-tools`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSnackbar({
          open: true,
          message: result.message || `Refreshed ${result.tools?.length || 0} tools from MCP servers`,
          severity: 'success'
        });
      } else {
        throw new Error(result.detail || result.message || 'Failed to refresh tools');
      }
    } catch (error: any) {
      toast({ title: error.message || 'Failed to refresh MCP tools', variant: 'destructive' });
    } finally {
      setRefreshingTools(false);
    }
  };

  const resetMcpForm = () => {
    setMcpForm({
      name: '',
      description: '',
      config: '{\n  "command": "npx",\n  "args": ["-y", "@package/server"],\n  "env": {}\n}',
    });
    setEditingMcp(null);
  };

  const openEditSheet = (server: MCPServer) => {
    setEditingMcp(server);
    setMcpForm({
      name: server.name,
      description: server.description || '',
      config: JSON.stringify(server.config, null, 2),
    });
    setShowMcpSheet(true);
  };

  const loadKBSettings = async () => {
    setKbLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/knowledge-base/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setKbSettings(data);
      }
    } catch (error) {
      console.error('Error loading KB settings:', error);
    } finally {
      setKbLoading(false);
    }
  };

  const saveKBSettings = async () => {
    setKbSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/knowledge-base/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(kbSettings)
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Knowledge Base settings saved successfully!', severity: 'success' });
        loadKBSettings(); // Reload to get updated settings
      } else {
        throw new Error('Failed to save KB settings');
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save Knowledge Base settings', severity: 'error' });
    } finally {
      setKbSaving(false);
    }
  };

  const selectedProvider = providers.find((p) => p.id === settings.llm_provider);
  const availableModels = selectedProvider?.models || [];

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Info Alert */}
        <Alert>
          <Brain className="h-4 w-4" />
          <AlertDescription>
            <strong>Converse Co-Pilot v4.0</strong> — Configure the LLM provider and MCP servers
            for the agent-facing AI assistant. This is separate from the customer-facing Captain AI.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* LLM Configuration */}
          <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Brain className="h-4 w-4 text-primary" />
                LLM Configuration
              </CardTitle>
              <CardDescription>Provider, model, and generation parameters</CardDescription>
            </CardHeader>
            <CardContent>
              {settingsLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Enable toggle */}
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <Label htmlFor="copilot-enabled" className="font-medium cursor-pointer">
                        Enable Co-Pilot
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Turn the AI assistant on or off for agents
                      </p>
                    </div>
                    <Switch
                      id="copilot-enabled"
                      checked={settings.enabled}
                      onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                    />
                  </div>

                  {/* LLM Provider */}
                  <div className="space-y-1.5">
                    <Label>LLM Provider</Label>
                    <Select
                      value={settings.llm_provider || 'none'}
                      onValueChange={(v) =>
                        setSettings({ ...settings, llm_provider: v === 'none' ? '' : v, llm_model: '' })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {providers.map((provider) => (
                          <SelectItem key={provider.id} value={provider.id}>
                            {provider.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Model */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label>Model</Label>
                      {settings.api_key && settings.api_key.trim() !== '' && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={fetchDynamicModels}
                              disabled={loadingModels}
                            >
                              {loadingModels ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Refresh models from provider</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <Select
                      value={settings.llm_model || 'none'}
                      onValueChange={(v) =>
                        setSettings({ ...settings, llm_model: v === 'none' ? '' : v })
                      }
                      disabled={!settings.llm_provider || loadingModels}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={loadingModels ? 'Loading models…' : 'Select model'}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {availableModels.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            {model.name} ({model.context_window.toLocaleString()} tokens)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* API Key */}
                  <div className="space-y-1.5">
                    <Label htmlFor="llm-api-key">API Key</Label>
                    <Input
                      id="llm-api-key"
                      type="password"
                      value={settings.api_key || ''}
                      onChange={(e) => setSettings({ ...settings, api_key: e.target.value })}
                      placeholder={settings.api_key_configured ? '••••••••••••' : 'Enter API key'}
                    />
                    <p className="text-xs text-muted-foreground">
                      {settings.api_key_configured
                        ? 'API key is configured. Enter a new value to update.'
                        : 'Your API key is encrypted and stored securely.'}
                    </p>
                  </div>

                  {/* Temperature */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Temperature</Label>
                      <span className="text-sm tabular-nums text-muted-foreground">
                        {settings.temperature}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={2}
                      step={0.1}
                      value={settings.temperature}
                      onChange={(e) =>
                        setSettings({ ...settings, temperature: parseFloat(e.target.value) })
                      }
                      className="w-full accent-primary"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0 (Focused)</span>
                      <span>1 (Balanced)</span>
                      <span>2 (Creative)</span>
                    </div>
                  </div>

                  {/* Max Tokens */}
                  <div className="space-y-1.5">
                    <Label htmlFor="llm-max-tokens">Max Tokens</Label>
                    <Input
                      id="llm-max-tokens"
                      type="number"
                      min={100}
                      max={1000000}
                      value={settings.max_tokens}
                      onChange={(e) =>
                        setSettings({ ...settings, max_tokens: parseInt(e.target.value) })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum tokens in response (up to 1M for supported models)
                    </p>
                  </div>

                  {/* System Prompt */}
                  <div className="space-y-1.5">
                    <Label htmlFor="llm-system-prompt">System Prompt (Optional)</Label>
                    <textarea
                      id="llm-system-prompt"
                      rows={3}
                      value={settings.system_prompt || ''}
                      onChange={(e) => setSettings({ ...settings, system_prompt: e.target.value })}
                      placeholder="Custom instructions for the Co-Pilot assistant…"
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                    />
                  </div>

                  <Button
                    onClick={saveCoPilotSettings}
                    disabled={settingsSaving}
                    className="w-full"
                  >
                    {settingsSaving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    {settingsSaving ? 'Saving…' : 'Save LLM Settings'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* MCP Servers */}
          <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Server className="h-4 w-4 text-primary" />
                MCP Servers
              </CardTitle>
              <CardDescription>Model Context Protocol server integrations</CardDescription>
              <CardAction>
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={refreshMcpTools}
                        disabled={refreshingTools || mcpServers.length === 0}
                      >
                        {refreshingTools ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {refreshingTools ? 'Refreshing…' : 'Refresh Tools'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Refresh tool definitions from all connected MCP servers
                    </TooltipContent>
                  </Tooltip>
                  <Button
                    size="sm"
                    onClick={() => {
                      resetMcpForm();
                      setShowMcpSheet(true);
                    }}
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Add Server
                  </Button>
                </div>
              </CardAction>
            </CardHeader>
            <CardContent>
              {mcpLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : mcpServers.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    No MCP servers configured. Add your first server to enable backend data access
                    for Co-Pilot.
                  </AlertDescription>
                </Alert>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-20">Enabled</TableHead>
                      <TableHead className="w-28">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mcpServers.map((server) => (
                      <TableRow key={server.id}>
                        <TableCell>
                          <p className="text-sm font-medium leading-none">{server.name}</p>
                          {server.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {server.description}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Switch
                                  checked={server.enabled}
                                  onCheckedChange={() => toggleMcpServerEnabled(server)}
                                  className="scale-90"
                                />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {server.enabled ? 'Click to disable' : 'Click to enable'}
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => testMcpServer(server.id)}
                                  disabled={testingServer === server.id}
                                >
                                  {testingServer === server.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Test Connection</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => openEditSheet(server)}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  onClick={() =>
                                    setDeleteMcpTarget({ id: server.id, name: server.name })
                                  }
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Knowledge Base Embedding Settings */}
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BookOpen className="h-4 w-4 text-primary" />
              Knowledge Base Embedding Settings
            </CardTitle>
            <CardDescription>
              Configure the embedding model for semantic document search
            </CardDescription>
          </CardHeader>
          <CardContent>
            {kbLoading ? (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Left: Provider + Model */}
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Embedding Provider</Label>
                    <Select
                      value={kbSettings.embedding_provider}
                      onValueChange={(v) => setKbSettings({ ...kbSettings, embedding_provider: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="local">Local (Sentence Transformers)</SelectItem>
                        <SelectItem value="openai">OpenAI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {kbSettings.embedding_provider === 'local' ? (
                    <div className="space-y-1.5">
                      <Label>Embedding Model</Label>
                      <Select
                        value={kbSettings.embedding_model}
                        onValueChange={(v) => {
                          const dimension = v.includes('mpnet-base-v2') ? 768 : 384;
                          setKbSettings({
                            ...kbSettings,
                            embedding_model: v,
                            embedding_dimension: dimension,
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paraphrase-MiniLM-L3-v2">
                            paraphrase-MiniLM-L3-v2 (384 dim, 61MB, fastest) ⚡ RECOMMENDED
                          </SelectItem>
                          <SelectItem value="sentence-transformers/all-MiniLM-L6-v2">
                            all-MiniLM-L6-v2 (384 dim, 90MB, fast)
                          </SelectItem>
                          <SelectItem value="sentence-transformers/all-mpnet-base-v2">
                            all-mpnet-base-v2 (768 dim, 400MB, accurate)
                          </SelectItem>
                          <SelectItem value="sentence-transformers/paraphrase-multilingual-MiniLM-L12-v2">
                            multilingual-MiniLM-L12-v2 (384 dim, multilingual)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="kb-openai-key">OpenAI API Key</Label>
                        <Input
                          id="kb-openai-key"
                          type="password"
                          value={kbSettings.openai_api_key || ''}
                          onChange={(e) =>
                            setKbSettings({ ...kbSettings, openai_api_key: e.target.value })
                          }
                          placeholder={kbSettings.has_openai_key ? 'API key configured' : 'sk-…'}
                        />
                        <p className="text-xs text-muted-foreground">
                          {kbSettings.has_openai_key
                            ? 'API key is configured'
                            : 'Enter your OpenAI API key'}
                        </p>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="kb-openai-model">OpenAI Model</Label>
                        <Input
                          id="kb-openai-model"
                          value={kbSettings.openai_model || 'text-embedding-ada-002'}
                          onChange={(e) =>
                            setKbSettings({ ...kbSettings, openai_model: e.target.value })
                          }
                          disabled
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: Chunking settings */}
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="max-chunk-tokens">Max Chunk Tokens</Label>
                    <Input
                      id="max-chunk-tokens"
                      type="number"
                      min={128}
                      max={2048}
                      value={kbSettings.max_chunk_tokens}
                      onChange={(e) =>
                        setKbSettings({
                          ...kbSettings,
                          max_chunk_tokens: parseInt(e.target.value),
                        })
                      }
                    />
                    <p className="text-xs text-muted-foreground">Maximum tokens per text chunk</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="chunk-overlap-tokens">Chunk Overlap Tokens</Label>
                    <Input
                      id="chunk-overlap-tokens"
                      type="number"
                      min={0}
                      max={512}
                      value={kbSettings.chunk_overlap_tokens}
                      onChange={(e) =>
                        setKbSettings({
                          ...kbSettings,
                          chunk_overlap_tokens: parseInt(e.target.value),
                        })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Overlap between chunks for context
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="kb-batch-size">Batch Size</Label>
                    <Input
                      id="kb-batch-size"
                      type="number"
                      min={1}
                      max={100}
                      value={kbSettings.batch_size}
                      onChange={(e) =>
                        setKbSettings({ ...kbSettings, batch_size: parseInt(e.target.value) })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Number of chunks to process at once
                    </p>
                  </div>

                  <Alert>
                    <AlertDescription className="text-xs">
                      Embedding dimension: <strong>{kbSettings.embedding_dimension}</strong>
                      {kbSettings.last_updated &&
                        ` · Last updated: ${new Date(kbSettings.last_updated).toLocaleString()}`}
                    </AlertDescription>
                  </Alert>
                </div>

                <div className="lg:col-span-2">
                  <Button
                    onClick={saveKBSettings}
                    disabled={kbSaving}
                    className="w-full sm:w-auto"
                  >
                    {kbSaving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    {kbSaving ? 'Saving…' : 'Save Knowledge Base Settings'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configuration Guide */}
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Code2 className="h-4 w-4 text-primary" />
              Configuration Guide
            </CardTitle>
            <CardDescription>MCP Server JSON configuration format reference</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm font-medium">MCP Server Configuration Format:</p>
            <pre className="rounded-lg bg-muted px-4 py-3 text-sm font-mono overflow-x-auto whitespace-pre">
{`{
  "command": "npx",
  "args": ["-y", "@modelcontextprotocol/server-postgres"],
  "env": {
    "POSTGRES_CONNECTION_STRING": "postgresql://user:pass@localhost/db"
  }
}`}
            </pre>
            <p className="text-xs text-muted-foreground">
              The command will be executed to start the MCP server. Environment variables can be
              used for secure configuration.
            </p>
          </CardContent>
        </Card>

        {/* MCP Server Sheet */}
        <Sheet
          open={showMcpSheet}
          onOpenChange={(open) => {
            setShowMcpSheet(open);
            if (!open) resetMcpForm();
          }}
        >
          <SheetContent className="sm:max-w-lg">
            <SheetHeader>
              <SheetTitle>{editingMcp ? 'Edit MCP Server' : 'Add MCP Server'}</SheetTitle>
              <SheetDescription>
                Configure a Model Context Protocol server for Co-Pilot tool access.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="mcp-name">Server Name *</Label>
                <Input
                  id="mcp-name"
                  value={mcpForm.name}
                  onChange={(e) => setMcpForm({ ...mcpForm, name: e.target.value })}
                  placeholder="e.g. PostgreSQL Server"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mcp-desc">Description</Label>
                <Input
                  id="mcp-desc"
                  value={mcpForm.description}
                  onChange={(e) => setMcpForm({ ...mcpForm, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mcp-config">Configuration (JSON) *</Label>
                <textarea
                  id="mcp-config"
                  rows={10}
                  value={mcpForm.config}
                  onChange={(e) => setMcpForm({ ...mcpForm, config: e.target.value })}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  MCP server configuration in JSON format
                </p>
              </div>
            </div>
            <SheetFooter className="mt-6 gap-2">
              <Button variant="outline" onClick={() => setShowMcpSheet(false)}>
                Cancel
              </Button>
              <Button onClick={saveMcpServer}>
                {editingMcp ? 'Update Server' : 'Create Server'}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Delete Confirmation */}
        <AlertDialog
          open={!!deleteMcpTarget}
          onOpenChange={(open) => !open && setDeleteMcpTarget(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete MCP Server</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete{' '}
                <strong>{deleteMcpTarget?.name}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteMcpServer}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

export default CoPilotSettingsTab;
